import { Component, inject, OnInit, signal, ViewChild, WritableSignal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ServicePortalApi } from '../../../api/api';
import { TokenMapper, TokenMapperInput } from '../../../api/types';
import {
  SynBreadcrumbComponent,
  SynBreadcrumbItemComponent,
  SynButtonComponent,
  SynDialogComponent,
  SynIconComponent,
} from '@synergy-design-system/angular';
import { globalClientId } from '../../state';
import { TokenMapperFormComponent } from './token-mapper-form.component';

@Component({
  selector: 'app-edit-token-mapper',
  template: `
    <syn-breadcrumb>
      <syn-breadcrumb-item routerLink="" i18n="@@home">Home</syn-breadcrumb-item>
      <syn-breadcrumb-item routerLink="/clients" i18n="@@clients">Clients</syn-breadcrumb-item>
      <syn-breadcrumb-item [routerLink]="['/', 'clients', clientId]">{{ globalClientId() ?? clientId }}
      </syn-breadcrumb-item>
      <syn-breadcrumb-item [routerLink]="['/', 'clients', clientId, 'mappers']">
        <span i18n="@@token-mappers">Token Mappers</span>
      </syn-breadcrumb-item>
      <syn-breadcrumb-item>{{ mapper()?.displayName ?? '' }}</syn-breadcrumb-item>
    </syn-breadcrumb>

    <div class="flex-row gap-2 align-center margin-bottom-1">
      <h1 i18n="@@edit-token-mapper">Edit Token Mapper</h1>
      <syn-button
        (click)="openDeleteDialog()"
        size="small"
      >
        <syn-icon name="delete_fill" slot="prefix" />
        <span i18n="@@delete-token-mapper">Delete Token Mapper</span>
      </syn-button>
    </div>

    <!-- Add loading indication until data is available -->
    @if (mapper()) {
      <app-token-mapper-form
        [mapper]="mapper()!"
        submitIcon="save"
        submitText="Save Changes"
        i18n-submitText="@@save-changes"
        (submitMapper)="onSubmit($event)"
      />
    }

    <syn-dialog
      label="Delete Token Mapper"
      i18n-label="@@delete-token-mapper"
      #deleteDialog>
      <div>
        <p i18n="@@delete-token-mapper-warning">
          Do you want to <strong>permanently delete</strong> the following token mapper?
        </p>
        <span>{{ this.mapper()?.displayName }}</span>
      </div>
      <div slot="footer" class="flex-row gap-1 justify-end">
        <syn-button i18n="@@cancel" (click)="closeDeleteDialog()">Cancel</syn-button>
        <syn-button variant="filled" (click)="onDeleteTokenMapper()">
          <span i18n="@@delete-token-mapper">Delete Token Mapper</span>
        </syn-button>
      </div>
    </syn-dialog>
  `,
  styles: [],
  imports: [
    SynButtonComponent,
    SynIconComponent,
    SynDialogComponent,
    RouterLink,
    SynBreadcrumbComponent,
    SynBreadcrumbItemComponent,
    TokenMapperFormComponent,

  ],
})
export class EditTokenMapperComponent implements OnInit {
  clientId = inject(ActivatedRoute).snapshot.params['id'];
  mapperId = inject(ActivatedRoute).snapshot.params['mapperId'];
  api = inject(ServicePortalApi);
  router = inject(Router);

  mapper: WritableSignal<TokenMapper | null> = signal(null);

  @ViewChild(SynDialogComponent) deleteDialog!: SynDialogComponent;

  ngOnInit() {
    this.api.getTokenMapper(this.clientId, this.mapperId, (value) => {
      this.mapper.set(value);
    });
  }

  openDeleteDialog() {
    void this.deleteDialog.nativeElement?.show();

  }

  closeDeleteDialog() {
    void this.deleteDialog.nativeElement?.hide();
  }

  onDeleteTokenMapper() {
    this.api.deleteTokenMapper(this.clientId, this.mapperId, () => {
      console.log('Client token mapper deleted', this.mapperId);
      void this.router.navigate(['clients', this.clientId, 'mappers']);
    });
  }

  onSubmit(mapper: TokenMapperInput) {
    this.api.updateTokenMapper(this.clientId, this.mapperId, mapper, (updatedMapper) => {
      this.mapper.set(updatedMapper);
    });
  }

  protected readonly globalClientId = globalClientId;
}
